import React, { useState } from 'react';
import {
  Box,
  Container,
  Tabs,
  Tab,
  Paper,
} from '@mui/material';
import { useUrlIntState } from '../hooks/useUrlState';
import {
  Dashboard,
  VideoLibrary,
  Analytics,
  TrendingUp,
  People,
  AttachMoney,
  Timeline,
  Insights,
  CalendarMonth
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import ReleaseCalendar from './ReleaseCalendar';
import { youtubeCalendarAPI } from '../services/api';

// Import sub-components
import SectionTitle from './crt/SectionTitle';
import ChannelOverview from './youtube/ChannelOverview';
import VideoAnalytics from './youtube/VideoAnalytics';
import AudienceInsights from './youtube/AudienceInsights';
import RevenueMetrics from './youtube/RevenueMetrics';
import PerformanceTrends from './youtube/PerformanceTrends';
import RealtimeMetrics from './youtube/RealtimeMetrics';
import ContentStrategy from './youtube/ContentStrategy';
import ReportingData from './youtube/ReportingData';
import DataBrowser from './youtube/DataBrowser';
import DiscoveryInsights from './youtube/DiscoveryInsights';
import RetentionInsights from './youtube/RetentionInsights';
import Recommendations from './youtube/Recommendations';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`youtube-tabpanel-${index}`}
      aria-labelledby={`youtube-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const YouTubeDashboard: React.FC = () => {
  // Tab persisted in URL — refresh keeps you on the same one.
  const [activeTab, setActiveTab] = useUrlIntState('yt_tab', 0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box mb={4}>
        <SectionTitle sub="&gt; Comprehensive analytics for your YouTube channel_">
          YOUTUBE :: ANALYTICS CONSOLE
        </SectionTitle>
      </Box>

      {/* Navigation Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab icon={<Dashboard />} label="Overview" />
          <Tab icon={<CalendarMonth />} label="Release calendar" />
          <Tab icon={<VideoLibrary />} label="Videos" />
          <Tab icon={<People />} label="Audience" />
          <Tab icon={<TrendingUp />} label="Performance" />
          <Tab icon={<AttachMoney />} label="Revenue" />
          <Tab icon={<Timeline />} label="Real-time" />
          <Tab icon={<Insights />} label="Strategy" />
          <Tab icon={<Analytics />} label="Reports" />
          <Tab icon={<Analytics />} label="Data" />
          <Tab icon={<Insights />} label="Discovery" />
          <Tab icon={<TrendingUp />} label="Retention" />
          <Tab icon={<Insights />} label="Recommendations" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={activeTab} index={0}>
        <ChannelOverview />
      </TabPanel>

      <TabPanel value={activeTab} index={1}>
        <YouTubeCalendarTab />
      </TabPanel>

      <TabPanel value={activeTab} index={2}>
        <VideoAnalytics />
      </TabPanel>

      <TabPanel value={activeTab} index={3}>
        <AudienceInsights />
      </TabPanel>

      <TabPanel value={activeTab} index={4}>
        <PerformanceTrends />
      </TabPanel>

      <TabPanel value={activeTab} index={5}>
        <RevenueMetrics />
      </TabPanel>

      <TabPanel value={activeTab} index={6}>
        <RealtimeMetrics />
      </TabPanel>

      <TabPanel value={activeTab} index={7}>
        <ContentStrategy />
      </TabPanel>

      <TabPanel value={activeTab} index={8}>
        <ReportingData />
      </TabPanel>

      <TabPanel value={activeTab} index={9}>
        <DataBrowser />
      </TabPanel>

      <TabPanel value={activeTab} index={10}>
        <DiscoveryInsights />
      </TabPanel>

      <TabPanel value={activeTab} index={11}>
        <RetentionInsights />
      </TabPanel>

      <TabPanel value={activeTab} index={12}>
        <Recommendations />
      </TabPanel>
    </Container>
  );
};

export default YouTubeDashboard;

const YT_LEGEND = [
  { type: 'short',                 label: 'Shorts',                color: '#00ffff' },
  { type: 'vod',                   label: 'VODs',                  color: '#00ff41' },
  { type: 'video_podcast_episode', label: 'Video-podcast episodes', color: '#ffb000' },
  { type: 'podcast_episode',       label: 'Audio podcast episodes', color: '#ff6600' },
];

const YouTubeCalendarTab: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['yt-calendar', 24],
    queryFn: () => youtubeCalendarAPI.get(24),
    staleTime: 5 * 60_000,
  });
  const colorFor = (t: string) => YT_LEGEND.find((l) => l.type === t)?.color || '#5aaa5a';
  const events = (data?.events || []).map((e: any) => ({
    date: e.date,
    title: e.title,
    color: colorFor(e.type),
    type: e.type,
    url: e.url || undefined,
  }));
  return (
    <Box sx={{ p: 1 }}>
      <ReleaseCalendar
        heading={`> ${events.length} releases tracked across the last 24 months`}
        events={events}
        legend={YT_LEGEND}
      />
    </Box>
  );
};
